/**
 * Trang Chi tiết vụ án + Timeline (Case Tracking) – Đương sự / nội bộ.
 */
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { ArrowBack, Event, Visibility, CheckCircle, Payment, Gavel, AutoAwesome, Description } from '@mui/icons-material'
import { PageDataState } from '../../components/PageDataState'
import { useGetCaseByIdQuery } from '../../store/api/casesApi'
import { useGetFilingsByCaseQuery } from '../../store/api/filingsApi'
import { useGetDocumentsByCaseQuery } from '../../store/api/documentsApi'
import type { CaseEvent } from '../../types/case'
import type { Document } from '../../types/document'
import { buildFullTimeline, type TimelineDisplayItem } from '../../utils/caseTimeline'
import { useState } from 'react'
import { useAppSelector } from '../../hooks/redux'

const statusLabels: Record<string, string> = {
  draft: 'Nháp', filed: 'Đã nộp', accepted: 'Đã thụ lý',
  pre_trial: 'Chuẩn bị xét xử', trial: 'Đang xét xử', closed: 'Đã kết thúc',
  returned: 'Trả đơn', transferred: 'Chuyển đơn',
}

const eventIcons: Record<string, string> = {
  filed: 'Thụ lý',
  accepted: 'Chấp nhận',
  assigned: 'Phân công',
  session: 'Phiên họp',
  decision: 'Quyết định',
  closed: 'Kết thúc',
}

const DOC_TYPE_LABELS: Record<string, string> = {
  notice_acceptance: 'Thông báo thụ lý',
  fee_notice: 'Giấy báo nộp tạm ứng án phí',
  summons: 'Giấy triệu tập',
  trial_decision: 'Bản án',
  other: 'Văn bản khác',
}

const FILING_TYPE_LABELS: Record<string, string> = {
  complaint: 'Đơn khởi kiện',
  appeal: 'Đơn kháng cáo',
  other: 'Đơn từ khác',
}

function CaseDetailPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { caseId } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const user = useAppSelector((s) => s.auth.user)
  const [docPreview, setDocPreview] = useState<Document | null>(null)
  const [litigantToast, setLitigantToast] = useState('')
  const [postponeOpen, setPostponeOpen] = useState(false)
  const [changeScheduleOpen, setChangeScheduleOpen] = useState(false)
  const [postponeReason, setPostponeReason] = useState('Có việc đột xuất, bận công tác nên không thể tham dự phiên đúng ngày. Kính đề nghị Tòa xem xét hoãn phiên.')
  const [scheduleReason, setScheduleReason] = useState('Đề nghị Tòa xem xét chuyển sang tuần sau do lý do sức khỏe. Có thể tham dự từ thứ Hai tuần tới.')
  const { data: caseDetail, isLoading, error, refetch } = useGetCaseByIdQuery(caseId!, { skip: !caseId })
  const { data: filings = [] } = useGetFilingsByCaseQuery(caseId!, { skip: !caseId })
  const { data: documents = [] } = useGetDocumentsByCaseQuery(caseId!, { skip: !caseId })

  const verdictDoc = documents.find((d) => d.type === 'trial_decision' || d.title?.includes('Bản án'))
  const isClosed = caseDetail?.status === 'closed'

  const isMarriageFamilyClosed = isClosed && (caseDetail?.category === 'marriage_family' || caseDetail?.case_code?.startsWith('HN-GD'))
  const evidencePlaceholder = isMarriageFamilyClosed
    ? [
        { party: 'Nguyên đơn', name: 'Giấy chứng nhận kết hôn' },
        { party: 'Nguyên đơn', name: 'Đơn xin ly hôn (bản scan)' },
        { party: 'Nguyên đơn', name: 'CMND/CCCD hai bên' },
        { party: 'Bị đơn', name: 'Biên lai tài sản chung (nếu có tranh chấp)' },
      ]
    : [
        { party: 'Nguyên đơn', name: 'Giấy chứng nhận kết hôn.pdf' },
        { party: 'Nguyên đơn', name: 'Đơn xin ly hôn (bản scan).pdf' },
        { party: 'Bị đơn', name: 'Biên lai tài sản chung.pdf' },
      ]

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', px: { xs: 0, sm: 0 } }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} size={isMobile ? 'medium' : 'medium'} sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
        Quay lại
      </Button>

      <PageDataState isLoading={isLoading} error={error} empty={!caseDetail && !isLoading} onRetry={() => refetch()}>
        {caseDetail && (
          <>
            <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 1 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h5" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                      {caseDetail.case_code}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip label={statusLabels[caseDetail.status] ?? caseDetail.status} color="primary" size="small" sx={{ fontSize: '0.75rem', height: 24 }} />
                      <Chip label={caseDetail.track === 'A' ? 'Online' : caseDetail.track === 'B' ? 'Hybrid' : 'Trực tiếp'} variant="outlined" size="small" sx={{ fontSize: '0.75rem', height: 24 }} />
                      {caseDetail.category === 'marriage_family' && (
                        <Chip label="Hôn nhân và gia đình" variant="outlined" size="small" sx={{ fontSize: '0.75rem', height: 24 }} />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                      Nguyên đơn: {caseDetail.plaintiff_name ?? caseDetail.plaintiff_id} · Bị đơn: {caseDetail.defendant_name ?? caseDetail.defendant_id}
                    </Typography>
                    {caseDetail.assigned_judge_name && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                        Thẩm phán: {caseDetail.assigned_judge_name}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, flexShrink: 0 }}>
                    Giá trị: {(caseDetail.claim_value_vnd / 1_000_000).toFixed(0)} tr
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {(user?.role === 'user' || user?.role === 'litigant') && caseDetail.next_hearing_at && (
              <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, borderColor: 'primary.light', border: 1 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>Phiên sắp tới</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                    {new Date(caseDetail.next_hearing_at).toLocaleString('vi-VN')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => setPostponeOpen(true)}>Xin hoãn phiên</Button>
                    <Button size="small" variant="outlined" onClick={() => setChangeScheduleOpen(true)}>Yêu cầu thay đổi lịch</Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
              <Event fontSize="small" /> Tiến trình vụ án
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Toàn bộ các bước từ nộp đơn đến kết thúc vụ án. Các bước chưa diễn ra hiển thị là « Chưa diễn ra ».
            </Typography>
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {buildFullTimeline(caseDetail.timeline ?? []).map((ev: TimelineDisplayItem) => (
                    <Box
                      key={ev.id}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        pb: 2,
                        borderLeft: '2px solid',
                        borderColor: ev.isPending ? 'grey.300' : 'primary.main',
                        ml: 1.5,
                        pl: 3,
                        position: 'relative',
                        opacity: ev.isPending ? 0.85 : 1,
                        '&:last-of-type': { pb: 0 },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -6,
                          top: 4,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: ev.isPending ? 'grey.400' : 'primary.main',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} color={ev.isPending ? 'text.secondary' : 'text.primary'}>
                          {ev.title}
                        </Typography>
                        {ev.description && (
                          <Typography variant="body2" color="text.secondary">{ev.description}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {ev.isPending
                            ? 'Chưa diễn ra'
                            : `${new Date(ev.at).toLocaleString('vi-VN')}${ev.user_name ? ` · ${ev.user_name}` : ''}`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {filings.length > 0 && (
              <>
                <Typography variant="h6" fontWeight={600} sx={{ mt: { xs: 2, sm: 3 }, mb: 1, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>Đơn từ</Typography>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    {filings.map((f) => (
                      <Box key={f.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                        <Typography variant="body2" fontWeight={500}>
                          {FILING_TYPE_LABELS[f.type] ?? f.type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Trạng thái: {f.status === 'accepted' ? 'Đã chấp nhận' : f.status === 'submitted' ? 'Đã nộp' : f.status} · Ngày nộp: {new Date(f.submitted_at || f.created_at).toLocaleDateString('vi-VN')}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </>
            )}

            <Typography variant="h6" fontWeight={600} sx={{ mt: { xs: 2, sm: 3 }, mb: 1, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>Chứng cứ đính kèm</Typography>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <List dense>
                  {evidencePlaceholder.map((e, i) => (
                    <ListItem key={i}>
                      <ListItemText primary={e.name} secondary={e.party} />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="caption" color="text.secondary">Chứng cứ do các bên nộp, lưu trong hồ sơ điện tử vụ án.</Typography>
              </CardContent>
            </Card>

            {isClosed && (
              <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                    Tóm tắt kết quả vụ án
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Vụ án đã được đưa ra xét xử. Bản án sơ thẩm đã được tuyên và gửi đến các bên. Các bên có quyền kháng cáo trong thời hạn 15 ngày kể từ ngày nhận được bản án.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {isClosed && verdictDoc && (user?.role === 'user' || user?.role === 'litigant') && (
              <Card sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.light' }}>
                <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
                    <Description color="primary" /> Bản án & Quyền kháng cáo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => setDocPreview(verdictDoc)}>
                      Xem bản án
                    </Button>
                  </Box>
                  <Alert severity="info" icon={<AutoAwesome />} sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Gợi ý quyền kháng cáo:</strong> Bạn có quyền kháng cáo bản án trong thời hạn <strong>15 ngày</strong> kể từ ngày tuyên án. Nếu kháng cáo, vụ án sẽ được chuyển Tòa cấp trên xét xử phúc thẩm.</Typography>
                  </Alert>
                  <Button variant="contained" color="primary" startIcon={<Gavel />} sx={{ mt: 1 }} onClick={() => setLitigantToast('Đơn kháng cáo đã được gửi. Hồ sơ sẽ chuyển giai đoạn phúc thẩm theo quy định.')}>
                    Nộp đơn kháng cáo
                  </Button>
                </CardContent>
              </Card>
            )}

            {documents.length > 0 && (
              <>
                <Typography variant="h6" fontWeight={600} sx={{ mt: { xs: 2, sm: 2 }, mb: 1, fontSize: { xs: '0.9375rem', sm: '1rem' } }}>Văn bản tố tụng</Typography>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    {documents.map((d) => (
                      <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, py: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{d.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{DOC_TYPE_LABELS[d.type] ?? d.type}</Typography>
                          <Chip label={d.status === 'sent' ? 'Đã gửi' : d.status} size="small" variant="outlined" sx={{ ml: 0.5, fontSize: '0.7rem' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button size="small" startIcon={<Visibility />} onClick={() => setDocPreview(d)}>Xem</Button>
                          {(user?.role === 'user' || user?.role === 'litigant') && d.status === 'sent' && (
                            <>
                              <Button size="small" variant="outlined" startIcon={<CheckCircle />} onClick={() => setLitigantToast('Đã xác nhận nhận được văn bản.')}>
                                Xác nhận đã nhận
                              </Button>
                              {d.type === 'fee_notice' && (
                                <Button size="small" variant="outlined" color="primary" startIcon={<Payment />} onClick={() => setLitigantToast('Đã ghi nhận xác nhận đóng phí tạm ứng.')}>
                                  Đã đóng phí
                                </Button>
                              )}
                              <Button size="small" variant="outlined" color="warning" startIcon={<Gavel />} onClick={() => setLitigantToast('Đơn khiếu nại đã được gửi. Tòa án sẽ xem xét và thông báo.')}>
                                Khiếu nại
                              </Button>
                            </>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
                {litigantToast && (
                  <Snackbar open={!!litigantToast} autoHideDuration={4000} onClose={() => setLitigantToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity="success" onClose={() => setLitigantToast('')}>{litigantToast}</Alert>
                  </Snackbar>
                )}
              </>
            )}

            <Dialog open={!!docPreview} onClose={() => setDocPreview(null)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {docPreview?.title}
                {docPreview && (
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight={400}>
                    {DOC_TYPE_LABELS[docPreview.type] ?? docPreview.type} · {docPreview.status === 'sent' ? 'Đã gửi' : docPreview.status}
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent sx={{ pt: 2, maxHeight: '70vh', overflow: 'auto' }}>
                {docPreview && (
                  <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.6 }}>{docPreview.content}</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDocPreview(null)}>Đóng</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={postponeOpen} onClose={() => setPostponeOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Xin hoãn phiên</DialogTitle>
              <DialogContent>
                <TextField fullWidth label="Lý do xin hoãn" multiline rows={3} value={postponeReason} onChange={(e) => setPostponeReason(e.target.value)} placeholder="Ví dụ: Có việc đột xuất, bận công tác..." sx={{ mt: 1 }} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPostponeOpen(false)}>Hủy</Button>
                <Button variant="contained" onClick={() => { setLitigantToast('Đơn xin hoãn phiên đã được gửi. Tòa án sẽ xem xét và thông báo.'); setPostponeOpen(false); setPostponeReason(''); }}>Gửi yêu cầu</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={changeScheduleOpen} onClose={() => setChangeScheduleOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Yêu cầu thay đổi lịch</DialogTitle>
              <DialogContent>
                <TextField fullWidth label="Nội dung yêu cầu" multiline rows={3} value={scheduleReason} onChange={(e) => setScheduleReason(e.target.value)} placeholder="Ngày/giờ đề xuất hoặc lý do cần đổi..." sx={{ mt: 1 }} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setChangeScheduleOpen(false)}>Hủy</Button>
                <Button variant="contained" onClick={() => { setLitigantToast('Yêu cầu thay đổi lịch đã được gửi. Thẩm phán sẽ xem xét.'); setChangeScheduleOpen(false); setScheduleReason(''); }}>Gửi yêu cầu</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </PageDataState>
    </Box>
  )
}

export default CaseDetailPage
